import {
    selectFilteredHotspotsByFlightId,
    selectSensitivityFilteredHotspotsByFlightId,
} from "features/entity-caching/hotspots/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { InspectorPanelAccordion } from "../../accordion/InspectorPanelAccordion"
import { InspectorPanelHotspotLayerFiltersTemperature } from "./InspectorPanelHotspotLayerFiltersTemperature"
import { InspectorPanelAccordionSubsections } from "../../accordion/InspectorPanelAccordionSubsections"
import { InspectorPanelHotspotLayerFiltersSize } from "./InspectorPanelHotspotLayerFiltersSize"
import { InspectorPanelHotspotLayerFiltersSensitivity } from "./InspectorPanelHotspotLayerFiltersSensitivity"
import { InspectorPanelHotspotLayerFiltersDistanceToPerimeter } from "./InspectorPanelHotspotLayerFiltersDistanceToPerimeter"
import { useEffect, useState } from "react"
import { AlertContainer } from "@nova/component-alert-container"
import { selectInfraredFlightById } from "features/entity-caching/flights/selectors"
import { updateInfraredFlight } from "features/pubsub/entityActions/flights/infrared"
import { useDebounceDispatch } from "tools/store"
import { HotspotStatus } from "@nova/constants"

export const InspectorPanelHotspotLayerFilters = ({
    id,
    openCategories,
    setCategoryIsOpen,
}: {
    id: string
    openCategories: Set<string>
    setCategoryIsOpen: (category: string, isOpen: boolean) => void
}) => {
    const { debounceDispatch } = useDebounceDispatch(500)
    const flight = useAppSelector(selectInfraredFlightById(id))
    const hotspotsFilteredBySensitivity = useAppSelector(
        selectSensitivityFilteredHotspotsByFlightId(flight?.id || "")
    ).filter((hotspot) => hotspot.status !== HotspotStatus.Removed)
    const filteredHotspots = useAppSelector(
        selectFilteredHotspotsByFlightId(flight?.id || "")
    ).filter((hotspot) => hotspot.status !== HotspotStatus.Removed)

    const extraFeaturesJSON = flight?.job?.extraFeatures

    const [resizerTrigger, triggerResize] = useState(0)

    useEffect(() => {
        triggerResize((prev) => ++prev)
    }, [flight])

    const shouldShowWarning = (localStorage.getItem("hotspot_filter_warning") || "false") !== "true"

    return (
        <InspectorPanelAccordion
            title="Filters"
            subtitle={`Showing ${filteredHotspots.length} of ${hotspotsFilteredBySensitivity.length} hotspots`}
            isOpen={openCategories.has("filters")}
            setIsOpen={(isOpen) => setCategoryIsOpen("filters", isOpen)}
            hasSubsections={true}
            shouldPadChildrenVertically={false}
            extraVerticalPaddingInRem={0}
            extraResizeDependency={resizerTrigger}
        >
            <InspectorPanelAccordionSubsections>
                {/* The boolean logic is repeated because InspectorPanelAccordionSubsections
                    doesn't understand children wrapped in a fragment. */}

                {/* Based on all hotspots. */}
                {!flight.skippedProcessing && !!extraFeaturesJSON?.includes("sens|") && (
                    <div className="flex flex-col w-full gap-2">
                        {shouldShowWarning && (
                            <AlertContainer
                                type="warning"
                                body="Filters you apply also apply for everyone on the project."
                                onClose={() => {
                                    localStorage.setItem("hotspot_filter_warning", "true")
                                    setTimeout(() => triggerResize((prev) => ++prev), 0)
                                }}
                            />
                        )}
                        <InspectorPanelHotspotLayerFiltersSensitivity
                            flight={flight}
                            updateFn={(updates) =>
                                debounceDispatch(updateInfraredFlight({ id, updates }))
                            }
                        />
                    </div>
                )}

                {/* Based on the subset of hotspots filtered by sensitivity. */}
                <InspectorPanelHotspotLayerFiltersDistanceToPerimeter
                    flight={flight}
                    updateFn={(updates) => debounceDispatch(updateInfraredFlight({ id, updates }))}
                    triggerResize={() => setTimeout(() => triggerResize((prev) => ++prev), 0)}
                    filteredHotspots={filteredHotspots}
                />

                {/* Based on the subset of hotspots filtered by sensitivity and distance to perimeter. */}
                {!flight.skippedProcessing && !!extraFeaturesJSON?.includes("hotspotThermal") && (
                    <InspectorPanelHotspotLayerFiltersTemperature
                        flight={flight}
                        updateFn={(updates) =>
                            debounceDispatch(updateInfraredFlight({ id, updates }))
                        }
                    />
                )}

                {/* Based on the subset of hotspots filtered by sensitivity and distance to perimeter. */}
                {!flight.skippedProcessing && !!extraFeaturesJSON?.includes("hotspotArea") && (
                    <InspectorPanelHotspotLayerFiltersSize
                        flight={flight}
                        updateFn={(updates) =>
                            debounceDispatch(updateInfraredFlight({ id, updates }))
                        }
                    />
                )}
            </InspectorPanelAccordionSubsections>
        </InspectorPanelAccordion>
    )
}
